import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useActionState } from "react";

import { authClient } from "../shared/lib/auth-client";
import { safeRedirect } from "../shared/lib/safe-redirect";

export const Route = createFileRoute("/auth")({
  /*
   * Optional, so every existing `navigate({ to: "/auth" })` keeps compiling —
   * a required search param here would break the sign-out path and the header.
   */
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const target = safeRedirect(search.redirect);
    return target ? { redirect: target } : {};
  },
  component: AuthPage,
});

type Mode = "sign-in" | "sign-up";

function AuthPage() {
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();
  const [mode, setMode] = useActionState<Mode, Mode>(
    (_prev, next) => next,
    "sign-in",
  );

  const [error, submit, isPending] = useActionState<string | null, FormData>(
    async (_previous, formData) => {
      // FormData.get can return a File; these inputs are text, but narrow
      // rather than String()-ing a possible File into "[object Object]".
      const field = (key: string): string => {
        const value = formData.get(key);
        return typeof value === "string" ? value : "";
      };

      const email = field("email");
      const password = field("password");
      const name = field("name");

      const result =
        mode === "sign-up"
          ? await authClient.signUp.email({ email, password, name })
          : await authClient.signIn.email({ email, password });

      if (result.error) {
        return (
          result.error.message ?? "Something went wrong. Please try again."
        );
      }

      /*
       * Back to wherever the guard interrupted, falling back to the listing.
       * `href` rather than `to` because the target is a validated string at
       * runtime, not a route literal the router can type-check.
       */
      if (redirect) {
        await navigate({ href: redirect });
      } else {
        await navigate({ to: "/events" });
      }
      return null;
    },
    null,
  );

  return (
    <main className="flex min-h-screen items-center justify-center p-6">
      <div className="w-full max-w-sm rounded-lg border border-border-subtle bg-surface p-6 shadow-sm">
        <h1 className="text-xl font-semibold">
          {mode === "sign-up" ? "Create an account" : "Sign in"}
        </h1>
        <p className="mt-1 text-sm text-content-muted">
          {mode === "sign-up" ? "Start planning your week." : "Welcome back."}
        </p>

        <form action={submit} className="mt-6 flex flex-col gap-4">
          {mode === "sign-up" && (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="name" className="text-sm font-medium">
                Name
              </label>
              <input
                id="name"
                name="name"
                autoComplete="name"
                required
                className="rounded-md border border-border-subtle bg-surface px-3 py-2 focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
              />
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label htmlFor="email" className="text-sm font-medium">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="rounded-md border border-border-subtle bg-surface px-3 py-2 focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="password" className="text-sm font-medium">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete={
                mode === "sign-up" ? "new-password" : "current-password"
              }
              required
              minLength={8}
              className="rounded-md border border-border-subtle bg-surface px-3 py-2 focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
            />
          </div>

          {error && (
            <p role="alert" className="text-sm text-brand">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isPending}
            className="rounded-md bg-brand px-4 py-2 font-medium text-brand-contrast hover:bg-brand-hover disabled:opacity-60"
          >
            {isPending
              ? "Working…"
              : mode === "sign-up"
                ? "Create account"
                : "Sign in"}
          </button>
        </form>

        <button
          type="button"
          onClick={() => {
            setMode(mode === "sign-up" ? "sign-in" : "sign-up");
          }}
          className="mt-4 text-sm text-content-muted underline hover:text-content"
        >
          {mode === "sign-up"
            ? "Already have an account? Sign in"
            : "Need an account? Sign up"}
        </button>
      </div>
    </main>
  );
}
